import type { CharacterState, Rules } from "../data/types";
import { calcDerivedStats } from "./characterCalc";

export interface UltimateRequirementItem {
  label: string;
  met: boolean;
}

export interface UltimateRequirementGroup {
  title: string;
  items: UltimateRequirementItem[];
}

export interface UltimateEligibility {
  groups: UltimateRequirementGroup[];
  metCount: number;
  totalCount: number;
  eligible: boolean;
}

// Checks the character against a ruleset's Ultimate Warlock requirements.
// Soul ownership and holding every Otherworldly Gift are shared by all rules;
// the Patron requirement varies by version, and only the current Thirteen
// (non-exiled Patrons) count.
export function checkUltimateEligibility(
  state: CharacterState,
  rules: Rules
): UltimateEligibility {
  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];
  const gifts = state.standaloneGifts || [];

  const activeOfferings = calcDerivedStats(state, rules).totalDarkOfferings;
  const soul: UltimateRequirementGroup = {
    title: "Have complete ownership of your own soul",
    items: [
      {
        label:
          activeOfferings === 0
            ? "Win back every Dark Offering"
            : `Win back every Dark Offering (${activeOfferings} still given away)`,
        met: activeOfferings === 0,
      },
    ],
  };

  const heldGiftIds = new Set([
    ...allPacts.map((p) => p.giftId),
    ...gifts.map((g) => g.giftId),
  ]);
  const allGifts: UltimateRequirementGroup = {
    title: `Possess all ${rules.otherworldlyGifts.length} Otherworldly Gifts`,
    items: rules.otherworldlyGifts.map((g) => ({
      label: g.name,
      met: heldGiftIds.has(g.id),
    })),
  };

  const qualifyingPatronIds = new Set<string | undefined>(
    rules.ultimateRequirement.kind === "gifts"
      ? [
          ...allPacts.filter((p) => p.giftId).map((p) => p.patronId),
          ...gifts.map((g) => g.patronId),
        ]
      : [
          ...allPacts.map((p) => p.patronId),
          ...gifts.map((g) => g.patronId),
          ...(state.resurrectionPacts || []).map((r) => r.patronId),
        ]
  );
  const action =
    rules.ultimateRequirement.kind === "gifts"
      ? "Hold a Gift from"
      : "Have dealings with";
  const patrons: UltimateRequirementGroup = {
    title: rules.ultimateRequirement.description,
    items: rules.patrons
      .filter((p) => !p.exiled)
      .map((p) => ({
        label: `${action} ${p.name}`,
        met: qualifyingPatronIds.has(p.id),
      })),
  };

  const groups = [soul, allGifts, patrons];
  const items = groups.flatMap((g) => g.items);
  const metCount = items.filter((i) => i.met).length;
  return {
    groups,
    metCount,
    totalCount: items.length,
    eligible: metCount === items.length,
  };
}
